import { FILE_TOO_LARGE_ERROR, NO_FILE_ERROR, timeUtil } from '@cmg/common';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FileDownloadOutlinedIcon,
  Grid,
  Link,
  LoadingButton,
  Stack,
  styled,
  Typography,
} from '@cmg/design-system';
import React from 'react';

import InfoIconTooltip from '../../offering-dl/shared/components/info-icon-tooltip/InfoIconTooltip';
import { OfferingProfile_GetParticipationRecordsQuery } from './graphql/__generated__/GetParticipationRecords';
import {
  dialogContentStyle,
  dialogRightPanelContentStyle,
  StyledList,
  StyledListItem,
} from './UploadParticipationSetModal.styles';

export type ImportSetDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onFileSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => void;
  selectedFile: File | null;
  error?: string;
  issuerName: string;
  participation?: OfferingProfile_GetParticipationRecordsQuery;
};
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});
export const UploadParticipationSetModal: React.FC<ImportSetDialogProps> = ({
  isOpen,
  onClose,
  onUpload,
  onFileSelected,
  selectedFile,
  error,
  issuerName,
  participation,
}) => {
  const fileNameColor = error && error === NO_FILE_ERROR ? 'error' : 'textPrimary';
  const maxFileSizeColor = error && error === FILE_TOO_LARGE_ERROR ? 'error' : 'textSecondary';
  const participationSets = participation?.participationSets?.items ?? [];
  const warningMessage =
    participationSets.length > 0
      ? `Offering data was last uploaded  ${timeUtil.formatAsMonthDateYear(
          participationSets[0].lastUploadedAt
        )} by ${participationSets[0].uploadedBy}. 
      Uploading a new one will replace your current offering data.`
      : null;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{`${issuerName}\u2013Upload Private Offering Data`}</DialogTitle>

      <DialogContent dividers sx={dialogContentStyle}>
        <Grid container spacing={2}>
          {!!warningMessage && (
            <Grid item xs={12}>
              <Alert severity="warning">{warningMessage}</Alert>
            </Grid>
          )}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">
                <AlertTitle>
                  An entry is required or has an invalid value for the following fields
                </AlertTitle>
                {error}
              </Alert>
            </Grid>
          )}
          <Grid item xs={6} padding={2}>
            <Stack gap={1}>
              Uploaded data is private to your firm. No one outside your organization will be able
              to see this data.
              <Stack direction="row" gap={2} alignItems="center">
                <Button variant="outlined" component="label">
                  Choose File
                  <VisuallyHiddenInput type="file" onChange={onFileSelected} accept=".csv" />
                </Button>
                <Typography color={fileNameColor}>
                  {selectedFile?.name ?? 'No file chosen'}
                </Typography>
              </Stack>
              <Typography color={maxFileSizeColor}>Maximum CSV file size: 30MB</Typography>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack gap={2} sx={dialogRightPanelContentStyle}>
              <Stack gap={1}>
                <Grid
                  container
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h3">Upload Details</Typography>
                  <Button variant="text" startIcon={<FileDownloadOutlinedIcon />}>
                    <Link variant="h4" href="../../../../../Participation_Sample.csv">
                      Sample File
                    </Link>
                  </Button>
                </Grid>
                The format of your CSV file is very important. Your column headers must match column
                names provided. Please refer to the information provided and sample file.
              </Stack>
              <Stack gap={1}>
                <Typography variant="subtitle1">Required Columns</Typography>
                <Stack direction="row" gap={1}>
                  <Chip
                    label={
                      <InfoIconTooltip
                        label="Investor Name"
                        tooltipContent={
                          <Typography variant="tooltip" fontWeight="bold">
                            Names Accepted
                            <Typography variant="tooltip">
                              <StyledList>
                                <StyledListItem>Investor Name</StyledListItem>
                                <StyledListItem>Investor</StyledListItem>
                                <StyledListItem>Account</StyledListItem>
                              </StyledList>
                            </Typography>
                          </Typography>
                        }
                      />
                    }
                    color="secondary"
                  />
                  <Chip
                    label={
                      <InfoIconTooltip
                        label="Allocation (Shares)"
                        tooltipContent={
                          <Typography variant="tooltip" fontWeight="bold">
                            Names Accepted
                            <Typography variant="tooltip">
                              <StyledList>
                                <StyledListItem>Allocation (Shares)</StyledListItem>
                                <StyledListItem>Allocation Quantity</StyledListItem>
                              </StyledList>
                            </Typography>
                          </Typography>
                        }
                      />
                    }
                    color="secondary"
                  />
                </Stack>
              </Stack>
              <Stack gap={1}>
                <Typography variant="subtitle1">Optional Columns</Typography>
                <Stack direction="row" gap={1}>
                  <Chip
                    label={
                      <InfoIconTooltip
                        label="Allocation $"
                        tooltipContent={
                          <Typography variant="tooltip" fontWeight="bold">
                            Names Accepted
                            <Typography variant="tooltip">
                              <StyledList>
                                <StyledListItem>Allocation $</StyledListItem>
                                <StyledListItem>Allocation ($)</StyledListItem>
                                <StyledListItem>Allocation (USD)</StyledListItem>
                              </StyledList>
                            </Typography>
                            <Box paddingTop={1.5}>
                              Notes: <Typography variant="tooltip">Must be in USD</Typography>
                            </Box>
                          </Typography>
                        }
                      />
                    }
                    color="secondary"
                    variant="outlined"
                  />

                  <Chip label="AUM ($M)" color="secondary" variant="outlined" />
                  <Chip
                    label={
                      <InfoIconTooltip
                        label="Indication (Shares)"
                        tooltipContent={
                          <Typography variant="tooltip" fontWeight="bold">
                            Names Accepted
                            <Typography variant="tooltip">
                              <StyledList>
                                <StyledListItem>Indication (Shares)</StyledListItem>
                                <StyledListItem>Indication Quantity</StyledListItem>
                                <StyledListItem>Indication</StyledListItem>
                                <StyledListItem>Demand</StyledListItem>
                              </StyledList>
                            </Typography>
                          </Typography>
                        }
                      />
                    }
                    color="secondary"
                    variant="outlined"
                  />
                </Stack>
                <Stack direction="row" gap={1}>
                  <Chip
                    label={
                      <InfoIconTooltip
                        label="Indication $"
                        tooltipContent={
                          <Typography variant="tooltip" fontWeight="bold">
                            Names Accepted
                            <Typography variant="tooltip">
                              <StyledList>
                                <StyledListItem>Indication $</StyledListItem>
                                <StyledListItem>Indication ($)</StyledListItem>
                                <StyledListItem>Indication (USD)</StyledListItem>
                                <StyledListItem>Demand $</StyledListItem>
                              </StyledList>
                            </Typography>
                            <Box paddingTop={1.5}>
                              Notes: <Typography variant="tooltip">Must be in USD</Typography>
                            </Box>
                          </Typography>
                        }
                      />
                    }
                    color="secondary"
                    variant="outlined"
                  />
                  <Chip label="Investor Type" color="secondary" variant="outlined" />
                  <Chip
                    label={
                      <InfoIconTooltip
                        label="Roadshow Participation"
                        tooltipContent={
                          <Typography variant="tooltip" fontWeight="bold">
                            Accepted Values:
                            <Typography variant="tooltip">Yes, No, Null</Typography>
                          </Typography>
                        }
                      />
                    }
                    color="secondary"
                    variant="outlined"
                  />
                </Stack>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
        <LoadingButton variant="contained" onClick={onUpload}>
          Upload
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
